import React from 'react';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { IconPendoInfo } from '../../../../../../components/Icons';
import './Trend.scss';
import { formatValue } from 'presentation/components/NewTable/NTCell/ValueFormatters/ValueFormatters';

export type TrendDirection = 'up' | 'down' | 'stable';

export type TrendSeverity = 'neutral' | 'positive' | 'warning' | 'negative';

export interface TrendData {
  label: string;
  value: string | number;
  unit?: string;
  unitPosition?: 'before' | 'after';
  message?: string;
  direction?: TrendDirection;
  severity?: TrendSeverity;
  comparison?: {
    previousValue: string | number;
    target?: string | number;
  };
}

export interface TrendProps {
  data: TrendData;
  size?: 'standard' | 'large';
  showInfoIcon?: boolean;
  onInfoClick?: (data?: TrendData, children?: React.ReactNode | null) => void;
  className?: string;
}

export const Trend: React.FC<TrendProps> = ({
  data,
  size = 'standard',
  showInfoIcon = true,
  onInfoClick,
  className,
}) => {
  const {
    label,
    value,
    unit,
    unitPosition = 'after',
    message,
    direction = 'stable',
    severity = 'neutral',
    comparison,
  } = data;

  // Format value based on magnitude and unit
  const formatValueWithUnit = (val: number, unit?: string): string => {
    if (unit === '$') {
      // For dollar values, use appropriate scaling
      if (Math.abs(val) >= 1000000) {
        // For values >= 1M, format as X.XXM
        return formatValue(val / 1000000, 2) + 'M';
      } else if (Math.abs(val) >= 1000) {
        // For values >= 1K but < 1M, format as X.XXK
        return formatValue(val / 1000, 1) + 'K';
      } else {
        // For smaller values, no special formatting
        return formatValue(val, 0);
      }
    } else {
      // For non-dollar values, use 1 decimal place
      return formatValue(val, 1);
    }
  };

  const formattedValue = typeof value === 'number' ? formatValueWithUnit(value, unit) : value;
  const formattedValueComparison =
    typeof comparison?.previousValue === 'number'
      ? formatValueWithUnit(comparison.previousValue, unit)
      : comparison?.previousValue;

  // Configure DOMPurify for safe HTML rendering
  const purifyConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'a', 'ul', 'ol', 'li', 'p', 'br', 'strike', 'del', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
    ADD_ATTR: ['target', 'rel'],
    KEEP_CONTENT: true,
  };

  // Sanitize the message content
  const sanitizedMessage = message ? DOMPurify.sanitize(message, purifyConfig) : '';
  const parsedMessage = { __html: sanitizedMessage };

  const handleInfoClick = () => {
    if (onInfoClick) {
      // Create a React element with trend details
      const trendContent = (
        <div className="trend-details">
          <div className="trend-details-value-row">
            <div className="trend-details-value-row-value">
              {formattedValue}
              {unitPosition === 'after' && unit}
            </div>
            from
            <div className="trend-details-value-row-comparison">
              {formattedValueComparison}
              {unitPosition === 'after' && unit}
            </div>
          </div>
          {message && <div className="trend-details-message" dangerouslySetInnerHTML={parsedMessage} />}
          <div className="trend-details-value-row-severity">{severity}</div>
        </div>
      );

      onInfoClick(data, trendContent);
    }
  };

  return (
    <div
      className={classNames('trend', `trend--${unit === '$' ? 'large' : size}`, `trend--${severity}`, className)}
      onClick={handleInfoClick}
      role="button"
      tabIndex={0}
      aria-label={`${label} trend information`}
    >
      <div className="trend__header">
        <span className="trend__label">{label}</span>
        {showInfoIcon && (
          <div className="trend__info-icon">
            <IconPendoInfo />
          </div>
        )}
      </div>

      <div className="trend__value-row">
        <div className="trend__arrows">
          <div
            className={classNames('trend__arrow', 'trend__arrow--up', { 'trend__arrow--active': direction === 'up' })}
          />
          <div
            className={classNames('trend__arrow', 'trend__arrow--down', {
              'trend__arrow--active': direction === 'down',
            })}
          />
        </div>

        <div className="trend__value-container">
          {unitPosition === 'before' && unit && <span className="trend__unit trend__unit--before">{unit}</span>}
          <span className="trend__value">{formattedValue}</span>
          {unitPosition === 'after' && unit && <span className="trend__unit trend__unit--after">{unit}</span>}
        </div>
      </div>

      <div className="trend__message" dangerouslySetInnerHTML={parsedMessage} />
    </div>
  );
};
